import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  findSelectedTransportItem,
  type ProposalBundle
} from '../../types/proposalBundles';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { TransportTimeRangeCell } from '../TransportTimeRangeCell';
import StateLabel from './StateLabel';

interface Props {
  bundle: ProposalBundle;
  includeTax: boolean;
  legLabel: string;
}

const LegBlock = ({ bundle, includeTax, legLabel }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const dateStr = formatRouteDate(bundle.route.departureDate);
  const routeInfo =
    bundle.route.origin && bundle.route.destination
      ? `${bundle.route.origin} → ${bundle.route.destination}`
      : null;
  const selectedItem = findSelectedTransportItem(bundle.items);
  const selectedItemDisplayPrice =
    selectedItem != null
      ? (() => {
          const baseFromApi = selectedItem.priceText?.trim() ?? '';
          const base =
            baseFromApi || formatPriceWithMark(includeTax ? selectedItem.priceWithTax : selectedItem.price);
          if (
            base != null &&
            bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD &&
            selectedItem.transport.isAir === true
          ) {
            return baseFromApi.length > 0 ? base : `${base}（往復）`;
          }
          return base ?? '-';
        })()
      : null;

  return (
    <div>
      <div style={styles.bundleHeader}>
        <span style={styles.categoryLabel}>{legLabel}</span>
        <StateLabel state={bundle.state} style={styles.stateLabel} />
      </div>
      {(routeInfo || dateStr) && (
        <div style={styles.routeInfo}>
          {routeInfo && <span>{routeInfo}</span>}
          {dateStr && <span style={styles.dateText}>（{dateStr}）</span>}
        </div>
      )}
      {selectedItem ? (
        <div style={styles.selectedItemInfo}>
          <div style={styles.selectedItemTitle}>
            {selectedItem.transport.flightName || selectedItem.title || '-'}
            {selectedItem.transport.seatClass && (
              <span style={styles.seatClass}>（{selectedItem.transport.seatClass}）</span>
            )}
          </div>
          {selectedItem.transport.departureTime != null && selectedItem.transport.arrivalTime != null && (
            <div style={styles.selectedItemTime}>
              <TransportTimeRangeCell
                departureTime={selectedItem.transport.departureTime}
                arrivalTime={selectedItem.transport.arrivalTime}
                departureDate={selectedItem.transport.departureDate}
                arrivalDate={selectedItem.transport.arrivalDate}
              />
            </div>
          )}
          <div style={styles.selectedItemPrice}>{selectedItemDisplayPrice}</div>
        </div>
      ) : null}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  bundleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px'
  },
  categoryLabel: {
    fontSize: '12px',
    fontWeight: 600 as const,
    flex: 1,
    color: theme.textColor
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 500 as const
  },
  routeInfo: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  dateText: {
    color: theme.grayTextColor
  },
  selectedItemInfo: {
    marginTop: '6px',
    padding: '8px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px'
  },
  selectedItemTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  seatClass: {
    fontSize: '12px',
    fontWeight: 400 as const
  },
  selectedItemTime: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginTop: '2px'
  },
  selectedItemPrice: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginTop: '4px'
  }
});

export default LegBlock;
